import { useRef, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useLazyLoadQuery } from "react-relay"
import { graphql } from "relay-runtime"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchInput, SuggestionSearch } from "@/components/ui/search"
import { useClickOutside } from "@/hooks/useClickOutside"
import { convertISOCodeToLanguage } from "@/lib/language-to-iso-code"
import {
  TreatmentPlanTemplateFilterInput,
  treatmentPlanTemplateSelectorQuery,
} from "@/modules/admin/treatment/components/__generated__/treatmentPlanTemplateSelectorQuery.graphql"
import { TreatmentForm } from "@/modules/admin/treatment/schema/treatment-form-schema"
import { SearchOption } from "@/modules/patients/components/search-option"

const treatmentQuery = graphql`
  query treatmentPlanTemplateSelectorQuery($filters: TreatmentPlanTemplateFilterInput) {
    treatmentPlanTemplates(where: $filters) {
      id
      countries
      name
      languages {
        language
        synonym
        title
        followUpInstructions
        recommendedTreatment
      }
      icdDiagnoses {
        id
        diagnosisCode
        text
      }
    }
  }
`

export const TreatmentPlanTemplateSelector = () => {
  const { t } = useTranslation()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const searchRef = useRef<HTMLInputElement>(null)
  const searchResultRef = useRef<HTMLDivElement>(null)
  const templateButtonRef = useRef<HTMLButtonElement>(null)

  const form = useFormContext<TreatmentForm>()
  const { reset } = form

  const {
    i18n: { language: currentLanguage },
  } = useTranslation()

  const globalTreatmentPlanTemplateFilters: TreatmentPlanTemplateFilterInput = {
    isGlobal: {
      eq: true,
    },
  }

  const { treatmentPlanTemplates } = useLazyLoadQuery<treatmentPlanTemplateSelectorQuery>(treatmentQuery, {
    filters: globalTreatmentPlanTemplateFilters,
  })

  const normalizedSearchValue = searchValue.length >= 3 ? searchValue.trim().toLowerCase() : ""
  const treatmentPlanTemplateOptions = treatmentPlanTemplates.map((template) => {
    const templateLanguage = template.languages.find(
      (i) => i.language === convertISOCodeToLanguage(currentLanguage.toUpperCase()),
    )

    return {
      id: template.id,
      name: template.name,
      title: templateLanguage?.title ?? "",
      synonym: templateLanguage?.synonym ?? "",
      language: templateLanguage?.language ?? "",
    }
  })

  const eligibleTreatmentPlanTemplates = treatmentPlanTemplateOptions
    ?.filter(
      (option) =>
        option.title.toLowerCase().includes(normalizedSearchValue) ||
        option.synonym.toLowerCase().includes(normalizedSearchValue),
    )
    .sort((a, b) => a.title.localeCompare(b.title))

  useClickOutside((event: MouseEvent) => {
    if (dropdownOpen && searchResultRef.current && !searchResultRef.current.contains(event.target as HTMLElement)) {
      setDropdownOpen(false)
    }
  }, searchRef)

  const handleTemplateSelect = (templateId: string) => {
    if (!templateId) return
    setSearchValue("")
    setDropdownOpen(false)

    const selectedTemplate = treatmentPlanTemplates.find((template) => template.id === templateId)
    if (!selectedTemplate) return

    const languageMap = new Map(selectedTemplate.languages.map((lang) => [lang.language, lang]))

    reset({
      name: selectedTemplate.name,
      countries: [...selectedTemplate.countries],
      diagnosisCodes: [...selectedTemplate.icdDiagnoses],
      // German fields
      germanTitle: languageMap.get("GERMAN")?.title || "",
      germanSynonyms: languageMap.get("GERMAN")?.synonym || "",
      germanRecommended: languageMap.get("GERMAN")?.recommendedTreatment || "",
      germanInstruction: languageMap.get("GERMAN")?.followUpInstructions || "",
      // French fields
      frenchTitle: languageMap.get("FRENCH")?.title || "",
      frenchSynonyms: languageMap.get("FRENCH")?.synonym || "",
      frenchRecommended: languageMap.get("FRENCH")?.recommendedTreatment || "",
      frenchInstruction: languageMap.get("FRENCH")?.followUpInstructions || "",
      // English fields
      englishTitle: languageMap.get("ENGLISH")?.title || "",
      englishSynonyms: languageMap.get("ENGLISH")?.synonym || "",
      englishRecommended: languageMap.get("ENGLISH")?.recommendedTreatment || "",
      englishInstruction: languageMap.get("ENGLISH")?.followUpInstructions || "",
      // Italian fields
      italianTitle: languageMap.get("ITALIAN")?.title || "",
      italianSynonyms: languageMap.get("ITALIAN")?.synonym || "",
      italianRecommended: languageMap.get("ITALIAN")?.recommendedTreatment || "",
      italianInstruction: languageMap.get("ITALIAN")?.followUpInstructions || "",
    })
  }

  return (
    <SuggestionSearch>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen} modal={false}>
        <DropdownMenuTrigger asChild ref={templateButtonRef}>
          <Button data-testid="clone-treatment-plan-button" variant="secondary" className="w-fit">
            {t("admin.treatment.cloneFromGlobalTemplates")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="rounded-lg" align="start" dataTestId="treatment_template_search_dialog">
          <div className="border-border border-b p-2">
            <SearchInput
              ref={searchRef}
              onKeyDown={(e) => {
                e.stopPropagation()
              }}
              data-testid="find-treatment-template-input"
              value={searchValue}
              placeholder={t("common.search")}
              autoComplete="off"
              onChange={({ target: { value } }) => {
                setSearchValue(value)
              }}
              onClear={() => {
                setSearchValue("")
              }}
            />
          </div>
          {eligibleTreatmentPlanTemplates && eligibleTreatmentPlanTemplates.length > 0 ? (
            <ScrollArea
              className="grid max-w-88.25 gap-0.5"
              data-testid="treatment-template-scroll-section"
              viewportClassName="max-h-84">
              <div className="py-4">
                {eligibleTreatmentPlanTemplates.map((option) => (
                  <DropdownMenuItem
                    key={option.name}
                    className="group px-2 py-0"
                    data-testid={option.name}
                    onSelect={() => handleTemplateSelect(option.id)}>
                    <SearchOption
                      className="group-data-highlighted:bg-surface-hovered block w-84.25"
                      value={option.name}
                      searchValue={searchValue}
                      title={option.title}
                      synonym={option.synonym}
                      onSelect={() => handleTemplateSelect(option.id)}
                    />
                  </DropdownMenuItem>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div
              className="text-on-surface-subdued relative cursor-default px-4 py-4 text-sm select-none"
              data-testid="nothing_found_treatment_template">
              {t("common.nothingFound")}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </SuggestionSearch>
  )
}
